<template>
  <div class="media-upload-field" :dir="isHebrew ? 'rtl' : 'ltr'">
    <label class="media-upload-label">{{ resolvedLabel }}</label>
    <div v-if="isUploading" class="media-upload-progress">
      <div class="media-upload-progress-bar" :style="{ width: uploadProgress + '%' }"></div>
      <span class="media-upload-progress-text">{{ Math.round(uploadProgress) }}%</span>
    </div>
    <div class="media-upload-container">
      <label class="media-upload-btn" :class="{ 'media-upload-btn-disabled': isUploading }">
        <input
          type="file"
          name="audioFile"
          accept="audio/*"
          class="media-upload-input"
          @change="handleFileChange"
          :id="inputId"
          :disabled="isUploading">
        <span class="media-upload-btn-text">{{ resolvedUploadButtonText }}</span>
      </label>
      <div v-if="fileUrl" class="media-upload-preview">
        <div class="media-upload-preview-inner">
          <audio
            controls
            class="media-upload-audio"
            :src="fileUrl">
            {{ t('audioTagNotSupported') }}
          </audio>
          <button
            @click="handleRemove"
            type="button"
            :id="removeButtonId"
            class="media-upload-remove"
            :aria-label="t('remove')">
            ×
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase/app';
import 'firebase/storage';
import { translationMixin } from '@/mixins/translationMixin';

export default {
  name: 'AudioUpload',
  mixins: [translationMixin],
  props: {
    // File URL (v-model)
    value: {
      type: String,
      default: null
    },
    // Label for the upload field
    label: {
      type: String,
      default: null
    },
    // Upload button text
    uploadButtonText: {
      type: String,
      default: null
    },
    // Storage folder path (e.g., 'videos_and_audios')
    storageFolder: {
      type: String,
      default: 'videos_and_audios'
    },
    // Entity ID (attraction ID, accommodation ID, etc.)
    entityId: {
      type: [String, Number],
      default: null
    },
    // Entity type prefix for filename (e.g., 'attraction_id', 'accommodation_id')
    entityTypePrefix: {
      type: String,
      default: 'attraction_id'
    },
    // Fallback ID if entityId is not available (e.g., ride ID)
    fallbackId: {
      type: [String, Number],
      default: null
    },
    // Unique ID for this component instance (for multiple instances on same page)
    instanceId: {
      type: String,
      default: 'audio'
    },
    // Maximum file size in MB
    maxSizeMB: {
      type: Number,
      default: 50
    }
  },
  data() {
    return {
      uploadProgress: 0,
      isUploading: false,
      isWaitingForUrl: false
    };
  },
  computed: {
    resolvedLabel() {
      return this.label !== null && this.label !== undefined ? this.label : this.t('audio');
    },
    resolvedUploadButtonText() {
      return this.uploadButtonText !== null && this.uploadButtonText !== undefined
        ? this.uploadButtonText
        : this.t('uploadAudio');
    },
    fileUrl: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    inputId() {
      return `audioFile_${this.instanceId}`;
    },
    removeButtonId() {
      return `removeAudio_${this.instanceId}`;
    }
  },
  methods: {
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    handleFileChange(e) {
      let files = e.target.files || e.dataTransfer.files;
      if (!files || files.length === 0) return;
      
      let fileData = files[0];
      
      // Validate file type
      if (!fileData.type.startsWith('audio/')) {
        alert(this.t('audioFileOnly'));
        e.target.value = ''; // Reset input
        return;
      }
      
      // Validate file size
      const maxSizeBytes = this.maxSizeMB * 1024 * 1024; // Convert MB to bytes
      if (fileData.size > maxSizeBytes) {
        const fileSizeFormatted = this.formatFileSize(fileData.size);
        const maxSizeFormatted = this.formatFileSize(maxSizeBytes);
        alert(this.t('fileSizeTooLarge', { fileSize: fileSizeFormatted, maxSize: maxSizeFormatted }));
        e.target.value = ''; // Reset input
        return;
      }
      
      this.uploadFile(fileData);
    },
    uploadFile(fileData) {
      var today = new Date();
      let myTime = today.toUTCString().split(', ')[1].split(' ').join('_');
      
      // Build filename prefix - prioritize entityId, fallback to fallbackId, or use 'new'
      let fileNamePrefix;
      if (this.entityId) {
        fileNamePrefix = `${this.entityTypePrefix}_${this.entityId}`;
      } else if (this.fallbackId) {
        fileNamePrefix = this.fallbackId.toString();
      } else {
        fileNamePrefix = 'new_' + this.entityTypePrefix.replace('_id', '');
      }
      
      // Clear the old file when starting a new upload
      this.fileUrl = null;
      this.uploadProgress = 0;
      this.isUploading = true;
      // Emit initial progress to notify parent that upload has started
      this.$emit('upload-progress', 1);
      
      const storageRef = firebase.storage().ref(
        `${this.storageFolder}/${fileNamePrefix}_${myTime}_${fileData.name}`
      ).put(fileData);

      storageRef.on(`state_changed`,
        snapshot => {
          this.uploadProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          this.$emit('upload-progress', this.uploadProgress);
        },
        error => {
          console.error('Audio upload error:', error.message);
          alert(this.t('audioUploadError') + error.message);
          this.isUploading = false;
          this.uploadProgress = 0;
          this.$emit('upload-error', error);
        },
        () => {
          this.uploadProgress = 100;
          this.isUploading = false;
          this.isWaitingForUrl = true;
          storageRef.snapshot.ref.getDownloadURL().then((url) => {
            this.fileUrl = url;
            this.isWaitingForUrl = false;
            this.$emit('uploaded', url);
          }).catch((error) => {
            console.error('Error getting download URL:', error);
            this.isWaitingForUrl = false;
            alert(this.t('audioGetUrlError') + error.message);
          });
        }
      );
    },
    handleRemove() {
      this.fileUrl = null;
      this.uploadProgress = 0;
      this.isUploading = false;
      this.isWaitingForUrl = false;
      this.$emit('removed');
    },
    isUploadInProgress() {
      return this.isUploading || this.isWaitingForUrl;
    }
  }
};
</script>

<style scoped>
.media-upload-field {
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: #fafafa;
  border-radius: 12px;
  border: 1px solid #eee;
}

.media-upload-label {
  display: block;
  font-weight: 600;
  font-size: 1.4rem;
  color: #0D2F29;
  margin-bottom: 0.75rem;
}

[dir="rtl"] .media-upload-label {
  text-align: right;
}

[dir="ltr"] .media-upload-label {
  text-align: left;
}

.media-upload-progress {
  position: relative;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.media-upload-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #07A0C3, #34D0B6);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.media-upload-progress-text {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
}

[dir="ltr"] .media-upload-progress-text {
  right: auto;
  left: 8px;
}

.media-upload-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.media-upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #07A0C3, #0590b0);
  color: white;
  border-radius: 10px;
  font-weight: 500;
  font-size: 1.4rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(7, 160, 195, 0.3);
}

.media-upload-btn:hover:not(.media-upload-btn-disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(7, 160, 195, 0.4);
}

.media-upload-btn-disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.media-upload-input {
  display: none;
}

.media-upload-preview {
  margin-top: 0.5rem;
}

.media-upload-preview-inner {
  position: relative;
  display: inline-block;
  padding: 1rem;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #eee;
}

.media-upload-audio {
  width: 100%;
  min-width: 260px;
  max-width: 320px;
  height: 40px;
}

.media-upload-remove {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.media-upload-remove:hover {
  background: rgba(200, 0, 0, 0.8);
}
</style>

